import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import {
    cellSelectionFeature,
    columnPinningFeature,
    columnResizingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    createSortedRowModel,
    makeStateUpdater,
    rowSortingFeature,
    tableFeatures,
    useTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { getCellText, getColumnLabel, renderTextCell } from './gridColumns';
import { measureColumnContentWidth } from './autosizeColumn';
import WatchlistGridHeader from './WatchlistGridHeader';
import WatchlistGridRow, { WatchlistGridSkeletonRows, WatchlistGridTrailingRow } from './WatchlistGridRow';
import WatchlistGridColumnMenu from './WatchlistGridColumnMenu';
import WatchlistGridColumnsPanel from './WatchlistGridColumnsPanel';
import WatchlistGridFillHandle from './WatchlistGridFillHandle';
import {
    watchlistGridClasses as c,
    watchlistGridClassNames as cn,
} from './watchlistGridStyles';
import './watchlistGrid.css';
import { escapeAttributeValue } from '../../data_table/dom';
import { observeScrollElementRect } from '../../data_table/virtualScroll';
import { cx } from '../../ui/classNames';
import { isMacPlatform } from '../spreadsheet/keyboardCommands';
import useSpreadsheetSelection, { activeSelectionRange } from '../spreadsheet/useSpreadsheetSelection';
import useSpreadsheetEditing from '../spreadsheet/useSpreadsheetEditing';
import SpreadsheetCellEditor from '../spreadsheet/SpreadsheetCellEditor';
import SpreadsheetFeedback from '../spreadsheet/SpreadsheetFeedback';

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});
const IS_MAC = isMacPlatform();
// A trailing row's pinned cell spans the pinned columns, at least this wide
// (within the visible width) so a search box and a name fit.
const TRAILING_LEAD_MIN_WIDTH = 320;
// The fill handle's live preview as one more table state slice
// (`table.atoms.fillPreview`): rows subscribe to it as they do to the cell
// selection, so a drag re-renders only the rows it enters or leaves.
const fillPreviewFeature = {
    getInitialState: (initialState) => ({ fillPreview: null, ...initialState }),
};
const watchlistGridFeatures = tableFeatures({
    columnVisibilityFeature,
    columnPinningFeature,
    columnSizingFeature,
    columnResizingFeature,
    rowSortingFeature,
    cellSelectionFeature,
    fillPreviewFeature,
    sortedRowModel: createSortedRowModel(),
});
// Every column without its own setting gets: its text as the cell (see
// gridColumns), a 50px minimum width for resizing and autosize, and blank
// values passed to its sort function, which places them (TanStack's default
// would sort undefined values last in ascending order).
const DEFAULT_COLUMN = Object.freeze({
    cell: renderTextCell,
    minSize: 50,
    sortUndefined: false,
});
// The grid itself re-renders for column sizing only; sorting and visibility
// arrive as props, and cell selection is read per row (see WatchlistGridRow).
const selectGridRenderState = (state) => ({
    columnSizing: state.columnSizing,
    columnResizing: state.columnResizing,
});
const TABBABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    '[tabindex]',
    '[contenteditable="true"]',
].join(', ');

const ALIGN_CLASS = {
    cell: { left: c.cellAlignLeft, center: c.cellAlignCenter, right: c.cellAlignRight },
    header: { left: c.headerCellAlignLeft, center: c.headerCellAlignCenter, right: c.headerCellAlignRight },
};
const ALIGNMENTS = new Set(['left', 'center', 'right']);
const alignmentOf = (value) => (ALIGNMENTS.has(value) ? value : 'left');

const defaultGetRowId = (row) => row.id;

// The next (direction 1) or previous (-1) tab stop outside `root`, so Tab at
// the end of a row leaves the grid instead of walking through its headers and
// cell controls. Without layout (tests), every candidate counts as visible.
function findTabStopOutside(root, direction) {
    const layoutAvailable = document.documentElement.getClientRects().length > 0;
    const isVisible = (element) => {
        if (!layoutAvailable) return true;
        return typeof element.checkVisibility === 'function'
            ? element.checkVisibility()
            : element.getClientRects().length > 0;
    };
    const candidates = Array.from(document.querySelectorAll(TABBABLE_SELECTOR)).filter((element) => (
        element.tabIndex >= 0
        && !root.contains(element)
        && !element.closest('[inert], [aria-hidden="true"]')
        && isVisible(element)
    ));
    if (direction > 0) {
        return candidates.find((element) => (
            root.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING
        )) || null;
    }
    return candidates.reverse().find((element) => (
        root.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING
    )) || null;
}

// One entry per visible column, pinned first, from its TanStack leaf header.
// `offset` is the column's left edge in the content, before pinned columns
// stick. Each entry carries the class names its header and cells share:
// the marker classes and the static Tailwind look (watchlistGridStyles).
function buildColumnLayout(headers, startColumnCount, selectionEnabled) {
    let offset = 0;
    return headers.map((header, index) => {
        const { column } = header;
        const { meta = EMPTY_OBJECT } = column.columnDef;
        const pinned = index < startColumnCount;
        const lastPinned = pinned && index === startColumnCount - 1;
        const width = column.getSize();
        const style = { width };
        if (pinned) style.left = column.getStart('start');
        const align = alignmentOf(meta.align);
        const headerAlign = alignmentOf(meta.headerAlign);
        const cellClasses = [
            c.cell,
            ALIGN_CLASS.cell[align],
            cn.cell,
            cn.cellAlign[align],
            pinned ? cn.cellPinned : cn.cellPosition,
            pinned && c.cellPinnedLeft,
            lastPinned && c.cellWithRightBorder,
            lastPinned && cn.withRightBorder,
        ];
        const entryOffset = offset;
        offset += width;
        return {
            index,
            field: column.id,
            column,
            header,
            pinned,
            lastPinned,
            headerAlign,
            offset: entryOffset,
            width,
            selectable: selectionEnabled && column.columnDef.enableCellSelection !== false,
            style,
            cellClassName: cx(...cellClasses),
            skeletonClassName: cx(...cellClasses, c.cellSkeleton, cn.cellSkeleton),
            headerClassName: cx(
                c.headerCell,
                ALIGN_CLASS.header[headerAlign],
                cn.headerCell,
                pinned ? cn.headerCellPinned : cn.headerCellPosition,
                pinned && c.headerCellPinnedLeft,
                lastPinned && c.headerCellWithRightBorder,
                lastPinned && cn.withRightBorder,
            ),
        };
    });
}

function GridViewport({
    scrollRef,
    virtualizerRef,
    headerProps,
    rows,
    trailingRows,
    onTrailingKeyDown,
    loading,
    height,
    rowHeight,
    columnHeaderHeight,
    overscan,
    columnLayout,
    contentWidth,
    getRowClassName,
    ariaLabel,
    rowSelection,
    getActiveCellId,
    gridProps,
    fillHandleProps,
}) {
    const rowCount = loading ? 0 : rows.length;
    const trailingRowCount = loading ? 0 : trailingRows.length;
    // Pinned columns stick while the grid scrolls sideways; the fill handle
    // follows them with the horizontal offset.
    const [scrollLeft, setScrollLeft] = useState(0);
    // The scroller's inner width (0 until measured), which a trailing row's
    // pinned cell never exceeds.
    const [viewportWidth, setViewportWidth] = useState(0);
    const viewportRect = useMemo(() => ({ width: 0, height }), [height]);
    const estimateRowSize = useCallback(() => rowHeight, [rowHeight]);
    const getRowKey = useCallback((index) => rows[index]?.id ?? index, [rows]);
    const observeViewportRect = useCallback((instance, onRect) => observeScrollElementRect(instance, (rect) => {
        onRect(rect);
        const width = instance.scrollElement?.clientWidth || 0;
        if (width > 0) setViewportWidth(width);
    }), []);
    const rowVirtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => scrollRef.current,
        estimateSize: estimateRowSize,
        getItemKey: getRowKey,
        overscan,
        // The sticky header shares the scroll element; body rows start below it.
        scrollMargin: columnHeaderHeight,
        scrollPaddingStart: columnHeaderHeight,
        initialRect: viewportRect,
        observeElementRect: observeViewportRect,
    });
    // The keyboard layer scrolls the active cell into view through it.
    virtualizerRef.current = rowVirtualizer;
    const skeletonRowCount = loading
        ? Math.max(0, Math.ceil((height - columnHeaderHeight - 3) / rowHeight))
        : 0;
    const pinnedWidth = columnLayout.reduce((total, entry) => total + (entry.pinned ? entry.width : 0), 0);
    const trailingLeadWidth = Math.min(
        Math.max(pinnedWidth, TRAILING_LEAD_MIN_WIDTH),
        viewportWidth || Infinity,
    );

    // Focus stays on the grid element; aria-activedescendant names the active
    // cell, so moving it never loses focus when rows are virtualized away.
    const renderScroller = (activeCellId) => (
        <div
            ref={scrollRef}
            role="grid"
            aria-label={ariaLabel}
            aria-rowcount={rowCount + 1 + trailingRowCount}
            aria-colcount={columnLayout.length}
            aria-busy={loading || undefined}
            aria-activedescendant={activeCellId || undefined}
            className={cx(c.scroller, cn.scroller, fillHandleProps && cn.scrollerSpreadsheet)}
            onScroll={fillHandleProps ? (event) => {
                const next = event.currentTarget.scrollLeft;
                if (next !== scrollLeft) setScrollLeft(next);
            } : undefined}
            {...gridProps}
        >
            <div className={cx(c.content, cn.content)} style={{ width: contentWidth }}>
                <WatchlistGridHeader {...headerProps} />
                {loading ? (
                    <WatchlistGridSkeletonRows
                        rowCount={skeletonRowCount}
                        height={rowHeight}
                        columnLayout={columnLayout}
                    />
                ) : (
                    <div
                        role="rowgroup"
                        className={cx(c.body, cn.body)}
                        style={{ height: rowVirtualizer.getTotalSize() }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const row = rows[virtualRow.index];
                            return (
                                <WatchlistGridRow
                                    key={row.id}
                                    row={row}
                                    rowIndex={virtualRow.index}
                                    top={virtualRow.start - columnHeaderHeight}
                                    height={rowHeight}
                                    columnLayout={columnLayout}
                                    selection={rowSelection}
                                    className={cx(
                                        c.row,
                                        cn.row,
                                        cn.bodyRow,
                                        virtualRow.index % 2 === 1 && c.rowOdd,
                                        virtualRow.index === 0 && c.rowFirst,
                                        getRowClassName?.(row.original),
                                    )}
                                />
                            );
                        })}
                    </div>
                )}
                {trailingRowCount > 0 && (
                    <div
                        role="rowgroup"
                        className={cx(c.trailing, cn.trailing)}
                        onKeyDown={onTrailingKeyDown}
                    >
                        {trailingRows.map((trailingRow, index) => {
                            const rowIndex = rowCount + index;
                            return (
                                <WatchlistGridTrailingRow
                                    key={trailingRow.key}
                                    kind={trailingRow.kind}
                                    rowIndex={rowIndex}
                                    height={rowHeight}
                                    columnCount={columnLayout.length}
                                    leadWidth={trailingLeadWidth}
                                    className={cx(
                                        c.row,
                                        cn.row,
                                        rowIndex % 2 === 1 && c.rowOdd,
                                        rowIndex === 0 && c.rowFirst,
                                        trailingRow.kind === 'add' ? c.rowAdd : c.rowPending,
                                    )}
                                >
                                    {trailingRow.content}
                                </WatchlistGridTrailingRow>
                            );
                        })}
                    </div>
                )}
                {fillHandleProps && !loading && (
                    <WatchlistGridFillHandle
                        {...fillHandleProps}
                        columnLayout={columnLayout}
                        scrollLeft={scrollLeft}
                        headerHeight={columnHeaderHeight}
                        rowHeight={rowHeight}
                        rowCount={rowCount}
                    />
                )}
            </div>
        </div>
    );
    return (
        <rowSelection.Subscribe source={rowSelection.atom} selector={getActiveCellId}>
            {renderScroller}
        </rowSelection.Subscribe>
    );
}

/**
 * The Watchlist grid: TanStack Table v9 for column/sort/visibility/sizing
 * state and TanStack Virtual for rows, in one scrolling list with a sticky
 * header and sticky left-pinned columns.
 *
 * `columns` are TanStack column defs. Each needs an `id`, a `header` (text,
 * or a function of the header context) and a `size`; value columns add an
 * `accessorFn`, and sortable ones a `sortFn` (ascending; blank values reach
 * it too). `cell(context)` renders custom content from `context.row.original`
 * and `context.getValue()`; `header` and `cell` functions are called as plain
 * render functions, not mounted as components, so their elements keep their
 * state when the column defs are rebuilt. `enableSorting`, `enableHiding`,
 * `enableResizing`, `enableCellSelection` and `minSize` (50 by default) /
 * `maxSize` work as in TanStack. `meta` holds the rest:
 * - label: plain-text name when `header` renders custom content
 * - formatText(value, row): a column's text without a `cell`, and what copy
 *   and fill read
 * - align / headerAlign: 'left' (default), 'center' or 'right'
 * - headerClassName, cellClassName (a string, or a function of the cell context)
 * - disableColumnMenu
 * - sortKind: 'number' labels the menu's sort items low/high instead of A/Z
 *
 * Sorting (`sorting` / `onSortingChange`) and column visibility
 * (`columnVisibility` / `onColumnVisibilityChange`) are controlled TanStack
 * state; a header click cycles ascending, descending, unsorted, one column at
 * a time. Rows are striped from their position in the list;
 * `getRowClassName(row)` adds classes from a row's original data.
 *
 * With a `spreadsheet` draft store (see useSpreadsheetEditing) the grid adds
 * Excel-style cell selection, keyboard navigation, in-cell editing,
 * clipboard, fill (Mod+D and the fill handle on the selection's corner) and
 * undo. Columns with `enableCellSelection: false` stay out of it. Given a
 * `commandsRef`, the grid keeps `commandsRef.current` set to the editing
 * layer's commands ({ applyEdits(edits, label, describe), paste(text) }, null
 * without a draft store), so tools outside the grid record their edits as one
 * undo step with the same Undo toast, and pastes elsewhere on the page reach
 * the selected cells.
 *
 * Rows can follow the table's rows, outside sorting, filtering, the cell
 * selection, copy and fill, and always rendered: `pendingRows`
 * ([{ id, content }], e.g. securities picked to add) and, with `addRow`
 * ({ disabled, render(controls) }), the "+ Add security" row. Each shows its
 * content in one cell pinned to the left edge. The grid owns whether the add
 * row is editing, so ArrowDown from the last row (or from the header when
 * there are no rows) opens it: `controls` are { editing, onEditingChange,
 * onNavigateUp } for AddSecurityRow.
 */
export default function WatchlistGrid({
    rows,
    columns,
    getRowId = defaultGetRowId,
    pinnedFields = EMPTY_ARRAY,
    sorting = EMPTY_ARRAY,
    onSortingChange,
    columnVisibility = EMPTY_OBJECT,
    onColumnVisibilityChange,
    getRowClassName,
    loading = false,
    height = 420,
    rowHeight = 38,
    columnHeaderHeight = 40,
    overscan = 5,
    noRowsLabel = 'No rows',
    spreadsheet = null,
    commandsRef = null,
    pendingRows = EMPTY_ARRAY,
    addRow = null,
    className,
    'aria-label': ariaLabel,
}) {
    const rootRef = useRef(null);
    const scrollRef = useRef(null);
    const virtualizerRef = useRef(null);
    const headerRef = useRef(null);
    const idPrefix = useId();
    const [columnMenu, setColumnMenu] = useState(null);
    const [columnsPanel, setColumnsPanel] = useState(null);
    const [focusedHeaderField, setFocusedHeaderField] = useState(null);
    const [addRowEditing, setAddRowEditing] = useState(false);
    // The add row is not rendered while loading; it comes back idle.
    if (loading && addRowEditing) setAddRowEditing(false);

    const columnsById = useMemo(
        () => new Map(columns.map((column) => [column.id, column])),
        [columns],
    );
    // Accessor values are cached per row instance; rebuild the rows whenever
    // the column definitions (and so their accessors) change.
    const data = useMemo(() => rows.slice(), [rows, columns]);
    // Attribute definitions can disappear during a context refresh. Keep the
    // parent's saved sort, but only pass columns currently present to TanStack.
    const availableSorting = useMemo(
        () => sorting.filter((entry) => columnsById.has(entry.id)),
        [sorting, columnsById],
    );
    const sortDirections = useMemo(() => Object.fromEntries(
        availableSorting.map((entry) => [entry.id, entry.desc ? 'desc' : 'asc']),
    ), [availableSorting]);
    const columnPinning = useMemo(() => ({
        start: pinnedFields.filter((field) => columnsById.has(field)),
        end: EMPTY_ARRAY,
    }), [columnsById, pinnedFields]);
    const resolveRowId = useCallback((row, index) => {
        const id = getRowId(row);
        return id == null ? String(index) : String(id);
    }, [getRowId]);

    const table = useTable({
        features: watchlistGridFeatures,
        columns,
        data,
        defaultColumn: DEFAULT_COLUMN,
        getRowId: resolveRowId,
        state: {
            sorting: availableSorting,
            columnVisibility,
            columnPinning,
        },
        onSortingChange,
        onColumnVisibilityChange,
        // Every column sorts ascending first (TanStack would start numbers
        // descending), and one column at a time.
        sortDescFirst: false,
        enableMultiSort: false,
        columnResizeMode: 'onChange',
        enableCellSelection: Boolean(spreadsheet),
        // One rectangular range, like Excel; Ctrl/Cmd+click does not add ranges.
        enableMultiCellRangeSelection: false,
        // Rows change with every live quote; a selection must survive that.
        autoResetCellSelection: false,
    }, selectGridRenderState);

    const startHeaders = table.getStartLeafHeaders();
    const centerHeaders = table.getCenterLeafHeaders();
    const { columnSizing, columnResizing } = table.state;
    const selectionEnabled = Boolean(spreadsheet);
    // Recomputed when sizes change: each entry carries the column's width.
    const columnLayout = useMemo(() => buildColumnLayout(
        [...startHeaders, ...centerHeaders],
        startHeaders.length,
        selectionEnabled,
    ), [startHeaders, centerHeaders, columnSizing, selectionEnabled]);
    const contentWidth = columnLayout.reduce((total, entry) => total + entry.column.getSize(), 0);
    const tabbableField = columnLayout.some((entry) => entry.field === focusedHeaderField)
        ? focusedHeaderField
        : columnLayout[0]?.field;
    const rowModelRows = table.getRowModel().rows;
    const rowIds = useMemo(() => rowModelRows.map((row) => row.id), [rowModelRows]);
    const columnIds = useMemo(() => columnLayout.map((entry) => entry.field), [columnLayout]);
    const isColumnSelectable = useCallback(
        (field) => selectionEnabled && columnsById.get(field)?.enableCellSelection !== false,
        [columnsById, selectionEnabled],
    );
    // Stable for the memoized rows: TanStack's atoms, Subscribe and selection
    // reads live on the table instance for its whole life.
    const rowSelection = useMemo(() => ({
        Subscribe: table.Subscribe,
        atom: table.atoms.cellSelection,
        readRanges: () => table.atoms.cellSelection.get() ?? EMPTY_ARRAY,
        getBounds: () => table.getCellSelectionBounds(),
        cellDomId: (rowIndex, columnIndex) => `${idPrefix}-cell-${rowIndex}-${columnIndex}`,
        fillPreviewAtom: table.atoms.fillPreview,
        readFillPreview: () => table.atoms.fillPreview.get(),
    }), [idPrefix, table.Subscribe, table.atoms, table.getCellSelectionBounds]);
    const selection = useSpreadsheetSelection({ table, rowIds, columnIds, isColumnSelectable });

    const findHeader = (field) => rootRef.current?.querySelector(
        `[role="columnheader"][data-field="${escapeAttributeValue(field)}"]`,
    );
    const focusHeader = (field) => {
        const header = findHeader(field);
        header?.focus();
        return Boolean(header);
    };
    const focusGrid = () => scrollRef.current?.focus({ preventScroll: true });
    // Horizontal half of keeping the active cell in view; headers are always
    // rendered, so they give every column's position.
    const scrollColumnIntoView = (field) => {
        const scroller = scrollRef.current;
        const entry = columnLayout.find((candidate) => candidate.field === field);
        const header = findHeader(field);
        if (!scroller || !entry || !header || entry.style.left !== undefined) return;
        const pinnedWidth = columnLayout
            .filter((candidate) => candidate.style.left !== undefined)
            .reduce((total, candidate) => total + candidate.column.getSize(), 0);
        const headerRect = header.getBoundingClientRect();
        const left = headerRect.left - scroller.getBoundingClientRect().left + scroller.scrollLeft;
        const right = left + headerRect.width;
        const visibleWidth = scroller.clientWidth - pinnedWidth;
        // A column wider than the space beside the pinned ones shows its start.
        if (left < scroller.scrollLeft + pinnedWidth || headerRect.width > visibleWidth) {
            scroller.scrollLeft = Math.max(0, left - pinnedWidth);
        } else if (right > scroller.scrollLeft + scroller.clientWidth) {
            scroller.scrollLeft = right - scroller.clientWidth;
        }
    };
    const readCellText = (rowId, field) => {
        const row = table.getRowModel().rowsById[rowId];
        const column = table.getAllLeafColumnsById()[field];
        if (!row || !column) return '';
        return getCellText(column, row) ?? '';
    };
    // Keyboard entry into the add row: open its search box, or focus the box
    // already open. False when there is no add row to enter.
    const enterAddRow = () => {
        if (!addRow || addRow.disabled || loading) return false;
        if (!addRowEditing) {
            setAddRowEditing(true);
            return true;
        }
        const input = rootRef.current?.querySelector(`.${c.rowAdd} input`);
        input?.focus();
        return Boolean(input);
    };
    const editing = useSpreadsheetEditing({
        config: spreadsheet,
        selection,
        rowIds,
        columnIds,
        isMac: IS_MAC,
        view: {
            readCellText,
            scrollToCell: (rowIndex, columnIndex) => {
                virtualizerRef.current?.scrollToIndex(rowIndex, { align: 'auto' });
                scrollColumnIntoView(columnIds[columnIndex]);
            },
            getCellElement: (rowId, field) => scrollRef.current?.querySelector(
                `[role="row"][data-id="${escapeAttributeValue(rowId)}"] [role="gridcell"][data-field="${escapeAttributeValue(field)}"]`,
            ) || null,
            focusGrid,
            focusHeader,
            // Mod+V: a focused, hidden textarea receives the browser's own
            // paste (its keydown is not cancelled), then the grid takes focus
            // back. `onPaste(null)` when the browser delivered nothing.
            capturePaste: (onPaste) => {
                const catcher = document.createElement('textarea');
                catcher.setAttribute('aria-hidden', 'true');
                catcher.tabIndex = -1;
                catcher.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;';
                document.body.appendChild(catcher);
                let settled = false;
                const settle = (text) => {
                    if (settled) return;
                    settled = true;
                    catcher.remove();
                    focusGrid();
                    onPaste(text);
                };
                catcher.addEventListener('paste', (event) => {
                    event.preventDefault();
                    settle(event.clipboardData?.getData('text/plain') ?? '');
                });
                catcher.focus({ preventScroll: true });
                window.setTimeout(() => settle(null), 0);
            },
            focusOutside: (direction) => {
                const target = rootRef.current && findTabStopOutside(rootRef.current, direction);
                if (target) target.focus();
                else scrollRef.current?.blur();
                return true;
            },
            getPageRowCount: () => Math.max(
                1,
                Math.floor(((scrollRef.current?.clientHeight || height) - columnHeaderHeight) / rowHeight),
            ),
            getFirstVisibleRowIndex: () => Math.ceil((scrollRef.current?.scrollTop || 0) / rowHeight),
            navigatePastEnd: enterAddRow,
        },
    });
    const getActiveCellId = (ranges) => {
        if (!selectionEnabled) return '';
        const range = activeSelectionRange(ranges);
        const position = range && selection.locate(range.anchorRowId, range.anchorColumnId);
        return position ? rowSelection.cellDomId(position.rowIndex, position.columnIndex) : '';
    };
    // Owner-side tools (the category rename) apply their edits through the
    // editing layer, so they become one undo step with the Undo toast.
    if (commandsRef) commandsRef.current = editing.commands;
    const fillHandleProps = selectionEnabled ? {
        selection: rowSelection,
        getBounds: selection.getBounds,
        disabled: Boolean(editing.editor),
        fill: editing.fill,
        onFillPreviewChange: makeStateUpdater('fillPreview', table),
        getScrollElement: () => scrollRef.current,
    } : null;

    // Without a direction (a header click) the sort takes TanStack's next
    // step; the column menu sets 'asc' or 'desc', or clears it with null.
    const applySort = (field, direction) => {
        const column = table.getAllLeafColumnsById()[field];
        if (!column?.getCanSort()) return;
        if (direction === undefined) column.toggleSorting();
        else if (direction === null) column.clearSorting();
        else column.toggleSorting(direction === 'desc');
        // Ranges are kept by row id; after a re-sort they would span different rows.
        editing.collapseSelection();
    };
    const openColumnMenu = (field, anchorEl) => {
        setColumnMenu((current) => (current?.field === field ? null : { field, anchorEl }));
    };
    const closeColumnMenu = ({ restoreFocus = false } = {}) => {
        const field = columnMenu?.field;
        setColumnMenu(null);
        if (restoreFocus && field) focusHeader(field);
    };

    const headerProps = {
        headerRef,
        columnLayout,
        height: columnHeaderHeight,
        sortDirections,
        tabbableField,
        openMenuField: columnMenu?.field ?? null,
        menuId: `${idPrefix}-column-menu`,
        getMenuButtonId: (index) => `${idPrefix}-column-menu-button-${index}`,
        resizingColumnId: columnResizing?.isResizingColumn || null,
        onHeaderClick: (event, field) => {
            // Ignore clicks bubbling (through React) from portals such as popovers.
            if (!event.currentTarget.contains(event.target)) return;
            applySort(field);
        },
        onHeaderKeyDown: (event, field, index) => {
            const content = event.currentTarget.querySelector(`.${c.headerTitleContent}`);
            if (content?.contains(event.target)) return;
            const lastIndex = columnLayout.length - 1;
            switch (event.key) {
                case 'ArrowLeft':
                    if (index > 0) focusHeader(columnLayout[index - 1].field);
                    break;
                case 'ArrowRight':
                    if (index < lastIndex) focusHeader(columnLayout[index + 1].field);
                    break;
                case 'Home':
                    focusHeader(columnLayout[0].field);
                    break;
                case 'End':
                    focusHeader(columnLayout[lastIndex].field);
                    break;
                case 'ArrowDown':
                    if (
                        !editing.enterBodyAt(field)
                        && !(rowModelRows.length === 0 && enterAddRow())
                    ) return;
                    break;
                case 'Enter':
                    if (event.ctrlKey || event.metaKey) {
                        const menuButton = event.currentTarget.querySelector(`.${c.headerMenu} button`);
                        if (menuButton) openColumnMenu(field, menuButton);
                    } else {
                        applySort(field);
                    }
                    break;
                case ' ':
                    break;
                default:
                    return;
            }
            event.preventDefault();
        },
        onHeaderFocus: (event, field) => {
            if (event.target === event.currentTarget) setFocusedHeaderField(field);
        },
        onMenuButtonClick: openColumnMenu,
        onResizeStart: (event, field) => {
            if (event.type === 'mousedown') {
                if (event.button !== 0) return;
                // Keep the drag from selecting header text.
                event.preventDefault();
            }
            event.stopPropagation();
            const entry = columnLayout.find((candidate) => candidate.field === field);
            entry?.header.getResizeHandler()(event);
        },
        onAutosize: (event, field) => {
            event.preventDefault();
            event.stopPropagation();
            const column = table.getColumn(field);
            if (!column || !rootRef.current) return;
            const width = measureColumnContentWidth(rootRef.current, field, {
                minWidth: column.columnDef.minSize,
                maxWidth: column.columnDef.maxSize,
            });
            table.setColumnSizing((current) => ({ ...current, [field]: width }));
        },
    };

    const trailingRows = pendingRows.map((pending) => ({
        key: `pending:${pending.id}`,
        kind: 'pending',
        content: pending.content,
    }));
    if (addRow) {
        trailingRows.push({
            key: 'add',
            kind: 'add',
            content: addRow.render({
                editing: addRowEditing,
                onEditingChange: (next) => setAddRowEditing(Boolean(next)),
                // ArrowUp from the idle row: back to the last row, else the header.
                onNavigateUp: () => {
                    if (!editing.enterLastRow() && tabbableField) focusHeader(tabbableField);
                },
            }),
        });
    }

    // Shift+Tab from the first control below the rows goes back to the last
    // row, the way Tab from its last cell came down.
    const onTrailingKeyDown = (event) => {
        if (event.key !== 'Tab' || !event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
        const firstTabStop = Array.from(event.currentTarget.querySelectorAll(TABBABLE_SELECTOR))
            .find((element) => element.tabIndex >= 0);
        if (event.target === firstTabStop && editing.enterLastRow()) event.preventDefault();
    };

    const editorColumn = editing.editor
        ? table.getAllLeafColumnsById()[editing.editor.columnId]
        : null;
    const menuIndex = columnMenu
        ? columnLayout.findIndex((entry) => entry.field === columnMenu.field)
        : -1;
    const menuColumn = menuIndex >= 0 ? columnLayout[menuIndex].column : null;
    const columnsWithMenu = columnLayout.filter(
        (entry) => !entry.column.columnDef.meta?.disableColumnMenu,
    );

    return (
        <div
            ref={rootRef}
            className={cx(c.root, cn.root, selectionEnabled && c.spreadsheet, className)}
        >
            <GridViewport
                scrollRef={scrollRef}
                virtualizerRef={virtualizerRef}
                headerProps={headerProps}
                rows={rowModelRows}
                trailingRows={trailingRows}
                onTrailingKeyDown={onTrailingKeyDown}
                loading={loading}
                height={height}
                rowHeight={rowHeight}
                columnHeaderHeight={columnHeaderHeight}
                overscan={overscan}
                columnLayout={columnLayout}
                contentWidth={contentWidth}
                getRowClassName={getRowClassName}
                ariaLabel={ariaLabel}
                rowSelection={rowSelection}
                getActiveCellId={getActiveCellId}
                gridProps={editing.gridProps}
                fillHandleProps={fillHandleProps}
            />
            {!loading && rowModelRows.length === 0 && noRowsLabel && (
                // Below the trailing rows, which stay usable.
                <div
                    className={cx(c.overlay, cn.overlay)}
                    style={{ top: columnHeaderHeight + (trailingRows.length * rowHeight) }}
                >
                    {noRowsLabel}
                </div>
            )}
            <WatchlistGridColumnMenu
                open={Boolean(menuColumn)}
                anchorEl={columnMenu?.anchorEl ?? null}
                column={menuColumn}
                menuId={`${idPrefix}-column-menu`}
                sortDirection={menuColumn ? sortDirections[menuColumn.id] || null : null}
                canHide={columnsWithMenu.length > 1}
                onSort={(direction) => {
                    applySort(columnMenu.field, direction);
                    closeColumnMenu({ restoreFocus: true });
                }}
                onHideColumn={() => {
                    onColumnVisibilityChange?.({
                        ...columnVisibility,
                        [columnMenu.field]: false,
                    });
                    setColumnMenu(null);
                }}
                onManageColumns={() => {
                    setColumnMenu(null);
                    setColumnsPanel({
                        anchorEl: headerRef.current,
                        initialColumnVisibility: columnVisibility,
                    });
                }}
                onClose={closeColumnMenu}
            />
            {columnsPanel?.anchorEl && (
                <WatchlistGridColumnsPanel
                    anchorEl={columnsPanel.anchorEl}
                    columns={table.getAllLeafColumns()}
                    columnVisibility={columnVisibility}
                    initialColumnVisibility={columnsPanel.initialColumnVisibility}
                    onColumnVisibilityChange={(next) => onColumnVisibilityChange?.(next)}
                    onClose={({ restoreFocus = false } = {}) => {
                        setColumnsPanel(null);
                        if (restoreFocus && tabbableField) focusHeader(tabbableField);
                    }}
                />
            )}
            {editing.editor && (
                <SpreadsheetCellEditor
                    key={editing.editor.key}
                    anchorEl={editing.editor.anchorEl}
                    anchorRect={editing.editor.anchorRect}
                    label={editing.editor.label || (editorColumn ? getColumnLabel(editorColumn) : '')}
                    inputLabel={editing.editor.inputLabel}
                    options={editing.editor.options}
                    value={editing.editor.value}
                    initialInput={editing.editor.initialInput}
                    isMac={IS_MAC}
                    onCommit={editing.editor.onCommit}
                    onCancel={editing.editor.onCancel}
                />
            )}
            {selectionEnabled && (
                <SpreadsheetFeedback feedback={editing.feedback} onClose={editing.closeFeedback} />
            )}
        </div>
    );
}
